/**
 * EisenhowerMatrix — priority (x) against impact (y) for the tasks that carry
 * both ratings, and a visible tray for the ones that do not.
 *
 * TWO HONESTY RULES DRIVE THIS COMPONENT.
 *
 * 1. An unrated task is never plotted. Ratings are 1–5 with NULL meaning "not
 *    set" (schemas.MIN_TASK_RATING / MAX_TASK_RATING), and dropping a NULL at
 *    the middle of an axis would put a number on the grid that the user never
 *    typed. Anything missing either rating goes to the tray, labelled with
 *    which rating is missing.
 * 2. "Deadline slack" is presented as the crude subtraction it is. It is
 *    (time until the deadline) − (remaining estimate) and nothing else: it
 *    knows nothing about hour classes, commitments or blocks already booked,
 *    so it is a ceiling, not a promise. The definition ships in the UI next to
 *    the number rather than in a doc nobody opens.
 *
 * Layout is a plain CSS grid with fractional columns — no measurement, no
 * ResizeObserver. The `setupTests.ts` stub never fires its callback, so a
 * width-measured grid would render nothing at all under test (the trap
 * BackfillTimeline documents at its own top).
 *
 * `now` is a prop rather than a `Date.now()` read so slack is deterministic:
 * the page owns one clock and every number on screen agrees with it.
 */
import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { alpha, useTheme } from '@mui/material/styles';

import { useLayoutTokens } from '../hooks/useLayoutTokens';

/** The slice of a task this matrix reads. Mirrors schemas.TaskResponse. */
export interface MatrixTask {
  id: number;
  title: string;
  status?: string | null;
  project_id?: number | null;
  project_title?: string | null;
  priority: number | null;
  impact: number | null;
  estimated_minutes: number | null;
  due_date: string | null;
}

export interface EisenhowerMatrixProps {
  tasks: MatrixTask[];
  /** The page's clock. Slack is measured against this instant. */
  now: Date;
  /** Run a scheduling PREVIEW scoped to this one task. Never writes. */
  onSchedule: (task: MatrixTask) => void;
  onOpenInProjects: (task: MatrixTask) => void;
  loading?: boolean;
  error?: string | null;
}

/** Ratings are 1–5; the quadrant split is at >= 3 (schemas, migration 0033). */
export const RATINGS = [1, 2, 3, 4, 5];
export const QUADRANT_SPLIT = 3;

/**
 * The definition shown beside every slack number. Exported so the test
 * compares the rendered text against this string rather than a paraphrase.
 */
export const DEADLINE_SLACK_DEFINITION =
  'Deadline slack = (time until the deadline) - (remaining estimate). ' +
  'It ignores your hour classes and everything already on your calendar, ' +
  'so it is a ceiling on how relaxed a task can be, not a promise that it fits.';

/**
 * Slack in whole minutes, or null when the inputs are not both there.
 *
 * "Remaining estimate" is the task's estimate. Minutes already kept live on
 * the blocks, not on the task, so this is the pessimistic reading — stated in
 * the definition above rather than quietly corrected here.
 */
export const computeDeadlineSlackMinutes = (
  task: MatrixTask,
  now: Date,
): number | null => {
  if (!task.due_date || task.estimated_minutes === null) return null;
  const due = new Date(task.due_date).getTime();
  if (Number.isNaN(due)) return null;
  return Math.round((due - now.getTime()) / 60000) - task.estimated_minutes;
};

/** "1h 30m" / "45m" / "0m". Sign is handled by the caller. */
const formatMinutes = (minutes: number): string => {
  const total = Math.abs(Math.round(minutes));
  const hours = Math.floor(total / 60);
  const rest = total % 60;
  if (hours === 0) return `${rest}m`;
  if (rest === 0) return `${hours}h`;
  return `${hours}h ${rest}m`;
};

const formatSignedMinutes = (minutes: number): string =>
  `${minutes < 0 ? '-' : ''}${formatMinutes(minutes)}`;

/** What the slack chip says, including the two "cannot be computed" cases. */
const slackChipLabel = (task: MatrixTask, now: Date): string => {
  if (!task.due_date) return 'No deadline';
  if (task.estimated_minutes === null) return 'No estimate';
  const slack = computeDeadlineSlackMinutes(task, now);
  if (slack === null) return 'No deadline';
  return `Slack ${formatSignedMinutes(slack)}`;
};

/** Why a task is in the tray, in the task's own words. */
const unratedReason = (task: MatrixTask): string => {
  if (task.priority === null && task.impact === null) return 'No priority, no impact';
  if (task.priority === null) return 'No priority';
  return 'No impact';
};

type QuadrantKey = 'do' | 'plan' | 'trap' | 'drop';

interface Quadrant {
  key: QuadrantKey;
  name: string;
  advice: string;
}

/**
 * The four quadrants, named for what this app can actually do about them.
 *
 * The classic matrix says "delegate" for the high-priority/low-impact corner.
 * This is a single-user app with nobody to delegate to, so saying it would be
 * advice the product cannot honour. The honest options there are to schedule
 * it anyway, drop it, or leave it in the backlog — which is what it says.
 */
const QUADRANTS: Record<QuadrantKey, Quadrant> = {
  do: {
    key: 'do',
    name: 'High priority, high impact',
    advice: 'Schedule these first.',
  },
  plan: {
    key: 'plan',
    name: 'Low priority, high impact',
    advice: 'Worth real time later — schedule deliberately, do not drift into it.',
  },
  trap: {
    key: 'trap',
    name: 'High priority, low impact',
    advice:
      'The classic matrix says delegate. There is nobody to delegate to in a ' +
      'solo app, so the honest choices are schedule it, drop it, or leave it ' +
      'in the backlog (change its status in Projects).',
  },
  drop: {
    key: 'drop',
    name: 'Low priority, low impact',
    advice: 'Leave it in the backlog, or drop it in Projects.',
  },
};

const quadrantOf = (priority: number, impact: number): QuadrantKey => {
  const highPriority = priority >= QUADRANT_SPLIT;
  const highImpact = impact >= QUADRANT_SPLIT;
  if (highPriority && highImpact) return 'do';
  if (!highPriority && highImpact) return 'plan';
  if (highPriority) return 'trap';
  return 'drop';
};

const EisenhowerMatrix: React.FC<EisenhowerMatrixProps> = ({
  tasks,
  now,
  onSchedule,
  onOpenInProjects,
  loading = false,
  error = null,
}) => {
  const theme = useTheme();
  const { panelPaddingSx, sectionGapSx } = useLayoutTokens();
  const [sortBySlack, setSortBySlack] = useState(false);

  const quadrantColor: Record<QuadrantKey, string> = {
    do: alpha(theme.palette.error.main, 0.1),
    plan: alpha(theme.palette.success.main, 0.1),
    trap: alpha(theme.palette.warning.main, 0.1),
    drop: alpha(theme.palette.text.primary, 0.04),
  };

  /** Nulls sort last: "unknown" is not "infinitely relaxed". */
  const bySlack = useMemo(
    () => (a: MatrixTask, b: MatrixTask) => {
      const left = computeDeadlineSlackMinutes(a, now);
      const right = computeDeadlineSlackMinutes(b, now);
      if (left === null && right === null) return a.title.localeCompare(b.title);
      if (left === null) return 1;
      if (right === null) return -1;
      return left - right;
    },
    [now],
  );

  const order = useMemo(
    () => (rows: MatrixTask[]) =>
      sortBySlack ? [...rows].sort(bySlack) : rows,
    [sortBySlack, bySlack],
  );

  const { plotted, unrated } = useMemo(() => {
    const withRatings: MatrixTask[] = [];
    const without: MatrixTask[] = [];
    tasks.forEach((task) => {
      if (task.priority !== null && task.impact !== null) withRatings.push(task);
      else without.push(task);
    });
    return { plotted: withRatings, unrated: without };
  }, [tasks]);

  const cells = useMemo(() => {
    const map = new Map<string, MatrixTask[]>();
    plotted.forEach((task) => {
      const key = `${task.priority}:${task.impact}`;
      const bucket = map.get(key);
      if (bucket) bucket.push(task);
      else map.set(key, [task]);
    });
    return map;
  }, [plotted]);

  const renderTask = (task: MatrixTask) => {
    const slack = computeDeadlineSlackMinutes(task, now);
    const overdueOnPaper = slack !== null && slack < 0;
    return (
      <Box
        key={task.id}
        data-testid={`matrix-task-${task.id}`}
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: 'background.paper',
          p: 0.75,
          mb: 0.5,
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
          {task.title}
        </Typography>
        {task.project_title && (
          <Typography variant="caption" color="text.secondary" display="block">
            {task.project_title}
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary" display="block">
          {task.estimated_minutes === null
            ? 'No estimate'
            : `Est. ${formatMinutes(task.estimated_minutes)}`}
        </Typography>
        <Chip
          size="small"
          variant="outlined"
          color={overdueOnPaper ? 'error' : 'default'}
          label={slackChipLabel(task, now)}
          sx={{ mt: 0.5 }}
        />
        <Stack direction="row" spacing={0} sx={{ mt: 0.25 }}>
          <Tooltip title="Preview a schedule for just this task">
            <IconButton
              size="small"
              aria-label={`Schedule ${task.title} now`}
              onClick={() => onSchedule(task)}
            >
              <ScheduleIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Open in Projects to edit ratings, estimate or status">
            <IconButton
              size="small"
              aria-label={`Open ${task.title} in Projects`}
              onClick={() => onOpenInProjects(task)}
            >
              <OpenInNewIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>
    );
  };

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={1}
        sx={sectionGapSx}
      >
        <Box>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography variant="h6" component="h2">
              Priority against impact
            </Typography>
            <Tooltip title={DEADLINE_SLACK_DEFINITION}>
              <IconButton size="small" aria-label="What deadline slack means">
                <InfoOutlinedIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Both ratings run 1–5 and the split is at 3. Only tasks you have
            rated are plotted.
          </Typography>
        </Box>
        <FormControlLabel
          control={
            <Switch
              checked={sortBySlack}
              onChange={(event) => setSortBySlack(event.target.checked)}
            />
          }
          label="Sort by deadline slack"
        />
      </Stack>

      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        {DEADLINE_SLACK_DEFINITION}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && (
        <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Loading tasks…
          </Typography>
        </Box>
      )}

      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        {(Object.keys(QUADRANTS) as QuadrantKey[]).map((key) => (
          <Stack key={key} direction="row" spacing={0.75} alignItems="center">
            <Box
              sx={{
                width: 14,
                height: 14,
                borderRadius: 0.5,
                border: 1,
                borderColor: 'divider',
                bgcolor: quadrantColor[key],
              }}
            />
            <Typography variant="caption" color="text.secondary">
              {QUADRANTS[key].name}
            </Typography>
          </Stack>
        ))}
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'auto repeat(5, minmax(0, 1fr))',
          gap: 0.5,
          alignItems: 'stretch',
        }}
      >
        {[5, 4, 3, 2, 1].map((impact) => (
          <React.Fragment key={`row-${impact}`}>
            <Box sx={{ display: 'flex', alignItems: 'center', pr: 1 }}>
              <Typography variant="caption" color="text.secondary" noWrap>
                Impact {impact}
              </Typography>
            </Box>
            {RATINGS.map((priority) => {
              const key = `${priority}:${impact}`;
              const quadrant = quadrantOf(priority, impact);
              return (
                <Box
                  key={key}
                  aria-label={`Priority ${priority}, impact ${impact}`}
                  sx={{
                    minHeight: 72,
                    p: 0.5,
                    borderRadius: 1,
                    border: 1,
                    borderColor: 'divider',
                    bgcolor: quadrantColor[quadrant],
                  }}
                >
                  {order(cells.get(key) ?? []).map(renderTask)}
                </Box>
              );
            })}
          </React.Fragment>
        ))}
        <Box />
        {RATINGS.map((priority) => (
          <Typography
            key={`axis-${priority}`}
            variant="caption"
            color="text.secondary"
            align="center"
          >
            Priority {priority}
          </Typography>
        ))}
      </Box>

      <Stack spacing={0.5} sx={{ mt: 1.5 }}>
        {(Object.keys(QUADRANTS) as QuadrantKey[]).map((key) => (
          <Typography key={key} variant="caption" color="text.secondary">
            <strong>{QUADRANTS[key].name}:</strong> {QUADRANTS[key].advice}
          </Typography>
        ))}
      </Stack>

      <Paper variant="outlined" sx={[panelPaddingSx, { mt: 2 }]}>
        <Typography variant="subtitle1" component="h3">
          Unrated — not plotted ({unrated.length})
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          These are missing a priority rating, an impact rating, or both.
          Dropping them at the middle of an axis would put a number on the grid
          that you never entered, so they wait here until you rate them in
          Projects.
        </Typography>
        {unrated.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Every task you have here carries both ratings.
          </Typography>
        ) : (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {order(unrated).map((task) => (
              <Box key={task.id} sx={{ minWidth: 180, maxWidth: 260 }}>
                {renderTask(task)}
                <Chip
                  size="small"
                  color="warning"
                  variant="outlined"
                  label={unratedReason(task)}
                />
              </Box>
            ))}
          </Stack>
        )}
      </Paper>

      {tasks.length === 0 && !loading && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          No open tasks were found in your projects.
        </Typography>
      )}
    </Box>
  );
};

export default EisenhowerMatrix;
